import React, { ComponentType, Fragment, useCallback, useEffect, useState } from "react";

import { faCirclePlus } from "@fortawesome/pro-solid-svg-icons/faCirclePlus";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { OptionalFields } from "@mapmycustomers/shared";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Button, ErrorRow } from "@mapmycustomers/ui";

import ValueEditorProps from "../../types/ValueEditorProps";

import styles from "./Breakpoints.module.scss";
import EditorComponentProps from "./EditorComponentProps";

const MAX_BREAKPOINTS_COUNT = 8;

const messages = defineMessages({
  addBreakpointLabel: {
    id: "map.colorization.createEditSidebar.styleEditor.breakpoints.addBreakpoint",
    defaultMessage: "Add break point",
    description: "Label of a new breakpoint field in the style editor in Pin Legends drawer",
  },
  addBreakpointTooltip: {
    id: "map.colorization.createEditSidebar.styleEditor.breakpoints.add.tooltip",
    defaultMessage: "Add break point",
    description: "Tooltip of the Add breakpoint button in the style editor in Pin Legends drawer",
  },
  duplicatesError: {
    id: "map.colorization.createEditSidebar.styleEditor.breakpoints.error.duplicate",
    defaultMessage: "Break point already in use",
    description: "Duplicate breakpoint error text in the style editor in Pin Legends drawer",
  },
  limitTooltip: {
    id: "map.colorization.createEditSidebar.styleEditor.breakpoints.limit.tooltip",
    defaultMessage:
      "Up to {count} {count, plural, one {breakpoint} other {breakpoints}} can be added",
    description:
      "Tooltip of the Remove breakpoint button in the style editor in Pin Legends drawer",
  },
  newBreakpointPlaceholder: {
    id: "map.colorization.createEditSidebar.styleEditor.breakpoints.addField.placeholder",
    defaultMessage: "Break point",
    description: "Placeholder of new breakpoint field in the style editor in Pin Legends drawer",
  },
  removeBreakpointTooltip: {
    id: "map.colorization.createEditSidebar.styleEditor.breakpoints.remove.tooltip",
    defaultMessage: "Remove break point",
    description:
      "Tooltip of the Remove breakpoint button in the style editor in Pin Legends drawer",
  },
});

export interface BreakpointsProps<T> extends OptionalFields<ValueEditorProps<T[]>, "value"> {
  comparator: (a: T, b: T) => number;
  editComponent: ComponentType<EditorComponentProps<T>>;
  onError?: (hasError: boolean) => void;
}

const Breakpoints = <T extends number | string>({
  comparator,
  editComponent: EditComponent,
  onChange,
  onError,
  value,
}: BreakpointsProps<T>) => {
  const intl = useIntl();

  const [newValue, setNewValue] = useState<T | undefined>();
  const [internalValues, setInternalValues] = useState<T[]>(value ?? []);
  useEffect(() => setInternalValues(value ?? []), [value]);

  const addValueFieldDisabled = (value?.length ?? 0) >= MAX_BREAKPOINTS_COUNT;

  const isDuplicate = useDynamicCallback((needle: T, atIndex?: number) => {
    if (!isDefined(atIndex)) {
      return internalValues.includes(needle);
    }
    return (
      (value ?? [])[atIndex] !== internalValues[atIndex] &&
      internalValues.some((value, index) => index !== atIndex && value === needle)
    );
  });

  const addValueDisabled = newValue === undefined || isDuplicate(newValue) || addValueFieldDisabled;

  const handleAddBreakpoint = useCallback(() => {
    if (!addValueDisabled) {
      onChange?.([...(value ?? []), newValue].sort(comparator));
      setNewValue(undefined);
    }
  }, [addValueDisabled, onChange, value, newValue, comparator]);

  const handleUpdateBreakpoint = useCallback((newValue: T, index: number) => {
    setInternalValues((values) => {
      // shouldn't really happen, but who knows...
      if (values.length <= index) {
        return values;
      }
      const newValues = [...values];
      newValues[index] = newValue;
      return newValues;
    });
  }, []);

  const handleRemoveBreakpoint = useCallback(
    (index: number) => {
      setInternalValues((values) => {
        const newValues = values.toSpliced(index, 1);
        onChange?.(newValues);
        return newValues;
      });
    },
    [onChange]
  );

  const uniqueValues = new Set(internalValues);
  const hasDuplicates = uniqueValues.size < internalValues.length;
  useEffect(() => onError?.(hasDuplicates), [hasDuplicates, onError]);

  const handleEnterOrBlur = useCallback(() => {
    if (!hasDuplicates) {
      onChange?.(internalValues.sort(comparator));
    }
  }, [comparator, hasDuplicates, internalValues, onChange]);

  return (
    <div className={styles.container}>
      <span className={styles.label}>{intl.formatMessage(messages.addBreakpointLabel)}</span>
      <div className={styles.item}>
        <Tooltip
          title={intl.formatMessage(messages.limitTooltip, {
            count: MAX_BREAKPOINTS_COUNT,
          })}
          trigger={addValueFieldDisabled ? "hover" : []}
        >
          {/* This div is needed because Tooltip doesn't appear over disabled fields */}
          <div className={styles.field}>
            <EditComponent
              disabled={addValueFieldDisabled}
              onChange={setNewValue}
              onEnter={handleAddBreakpoint}
              placeholder={intl.formatMessage(messages.newBreakpointPlaceholder)}
              value={newValue}
            />
          </div>
        </Tooltip>
        <Tooltip placement="left" title={intl.formatMessage(messages.addBreakpointTooltip)}>
          <Button
            className={cn(styles.action, styles.add)}
            disabled={addValueDisabled}
            icon={<FontAwesomeIcon icon={faCirclePlus} />}
            iconOnly
            onClick={handleAddBreakpoint}
            type="link"
          />
        </Tooltip>
      </div>
      {!!internalValues.length && <div className={styles.divider} />}
      {internalValues.map((value: T, index: number) => {
        const duplicate = isDuplicate(value, index);
        return (
          <Fragment key={index}>
            <div className={styles.item}>
              <EditComponent
                className={styles.field}
                duplicate={duplicate}
                onBlur={handleEnterOrBlur}
                onChange={(value: T) => handleUpdateBreakpoint(value, index)}
                onEnter={handleEnterOrBlur}
                value={value}
              />
              <Tooltip
                placement="left"
                title={intl.formatMessage(messages.removeBreakpointTooltip)}
              >
                <Button
                  className={styles.action}
                  icon={<FontAwesomeIcon icon={faXmark} />}
                  iconOnly
                  onClick={() => handleRemoveBreakpoint(index)}
                  type="link"
                />
              </Tooltip>
            </div>
            {duplicate && (
              <ErrorRow className={styles.error}>
                {intl.formatMessage(messages.duplicatesError)}
              </ErrorRow>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export default Breakpoints;
